import Close from "@mui/icons-material/Close";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Search from "@mui/icons-material/Search";
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { GfgTextField } from "@/components/ui/GfgTextField";
import { PHONE_COUNTRIES, PHONE_FALLBACK } from "@/constants/redemption";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import type { PhoneCountry } from "@/interfaces/redemption";
import { formatPhone } from "@/utils/phone-helper";

interface RedemptionPhoneFieldProps {
  value: string;
  /** Reports the formatted number and its dial code (for the confirmation review). */
  onChange: (formatted: string, dialCode: string, iso: string) => void;
  /** Red border on the number input for a failed-validation state. */
  error?: boolean;
  /** Error message (shown + announced when `error` is true). */
  errorText?: string;
  /** ISO-2 to initialise the calling-code picker with — aligns the dial code with
   *  the shipping country. Falls back to US if unknown. Stays editable afterwards. */
  initialCountry?: string;
}

/**
 * Phone number field with a fused, searchable country-code picker + libphonenumber
 * as-you-type formatting — owned Tailwind twin of the prototype's `PhoneField`.
 * Desktop: anchored dropdown; phone (≤sm): bottom sheet.
 */
export default function RedemptionPhoneField({
  value,
  onChange,
  error = false,
  errorText,
  initialCountry,
}: RedemptionPhoneFieldProps) {
  const [country, setCountry] = useState<PhoneCountry>(
    () =>
      PHONE_COUNTRIES.find((c) => c.iso === initialCountry) ??
      PHONE_COUNTRIES.find((c) => c.iso === "US") ??
      PHONE_FALLBACK,
  );
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(-1);
  const groupRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  // Phone tier matches the ≤640px CSS: below it, the picker is a bottom sheet.
  const isPhone = useMediaQuery("(max-width: 640px)");
  // Q5: below 1280 the hint collides with the error text, so it hides only then.
  const isBelowXl = useMediaQuery("(max-width: 1279px)");
  useFocusTrap(open && isPhone, sheetRef);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const dq = q.replace("+", "");
    if (!q) return PHONE_COUNTRIES;
    return PHONE_COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().indexOf(q) !== -1 ||
        c.iso.toLowerCase().indexOf(q) !== -1 ||
        c.dial.replace("+", "").indexOf(dq) !== -1,
    );
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (groupRef.current && !groupRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [open]);

  // Phone sheet: lock the scroll surface behind it + close on Escape.
  useEffect(() => {
    if (!open || !isPhone) return;
    const scroller = groupRef.current?.closest<HTMLElement>("[data-scroll-lock]") ?? document.body;
    const prev = scroller.style.overflow;
    scroller.style.overflow = "hidden";
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      scroller.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, isPhone]);

  // Keep the closed bottom sheet out of the tab order. Set `inert` imperatively
  // (HTMLElement.inert is in lib.dom) — the JSX `inert` attribute isn't in this
  // React version's prop types.
  useEffect(() => {
    if (sheetRef.current) sheetRef.current.inert = !open;
  }, [open]);

  const toggleMenu = () => {
    if (open) {
      setOpen(false);
      return;
    }
    setQuery("");
    setActive(-1);
    setOpen(true);
    // Auto-focus search on desktop only — on phone it would immediately raise the
    // keyboard and cover the sheet; let the user tap the search field instead.
    if (!isPhone) setTimeout(() => searchRef.current?.focus(), 0);
  };

  const pickCountry = (c: PhoneCountry) => {
    setCountry(c);
    setOpen(false);
    onChange(formatPhone(value, c.iso), c.dial, c.iso); // re-format existing digits for the new country
  };

  // Number input change. Backspacing a formatting character (e.g. the ")" in
  // "(415)") shortens the string without changing the digits — reformatting would
  // just re-add the character, trapping the caret so the area code can't be
  // deleted. Detect that case and drop the trailing digit instead, so backspace
  // chews through the whole number normally.
  const handleNumberInput = (raw: string) => {
    const prevDigits = value.replace(/\D/g, "");
    let nextDigits = raw.replace(/\D/g, "");
    if (raw.length < value.length && nextDigits === prevDigits) {
      nextDigits = nextDigits.slice(0, -1);
    }
    onChange(formatPhone(nextDigits, country.iso), country.dial, country.iso);
  };

  const onSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0 && filtered[active]) pickCountry(filtered[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative mb-4 flex items-stretch" ref={groupRef}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select country calling code"
        onClick={toggleMenu}
        className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-l-lg border border-smoke border-r-0 bg-white px-2.5 font-lato text-[14px] text-rodeo-black transition-colors hover:bg-light-dust"
      >
        <span className="text-[18px] leading-none">{country.flag}</span>
        <span className="font-semibold">{country.dial}</span>
        <ExpandMore className="text-graphite" sx={{ fontSize: 18 }} />
      </button>
      <GfgTextField
        className="min-w-0 flex-1"
        inputClassName="rounded-l-none"
        label="Phone number"
        hint={error && isBelowXl ? undefined : "— for delivery questions"}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        error={error}
        errorText={errorText}
        ignorePasswordManagers
        value={value}
        onChange={(e) => handleNumberInput(e.target.value)}
      />

      {/* Desktop: anchored dropdown. */}
      {!isPhone && open && (
        <div className="absolute top-[calc(100%+4px)] left-0 z-60 w-72.5 max-w-full overflow-hidden rounded-card border border-card-border bg-white shadow-[0_14px_34px_rgba(51,44,34,0.14)]">
          <div className="flex items-center gap-2 border-card-border border-b px-3 py-2.5">
            <Search className="text-dark-dust" sx={{ fontSize: 18 }} />
            {/* Combobox controlling the country listbox; announces the
                arrow-highlighted option via aria-activedescendant. */}
            <input
              ref={searchRef}
              type="text"
              placeholder="Search countries"
              autoComplete="off"
              role="combobox"
              aria-expanded={open}
              aria-controls="iv-cc-list-desktop"
              aria-autocomplete="list"
              aria-activedescendant={active >= 0 ? `iv-cc-opt-d-${active}` : undefined}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(-1);
              }}
              onKeyDown={onSearchKeyDown}
              className="flex-1 border-none bg-transparent font-lato text-[14px] text-rodeo-black outline-none"
            />
          </div>
          <div
            id="iv-cc-list-desktop"
            role="listbox"
            aria-label="Country calling codes"
            className="max-h-60 overflow-y-auto"
          >
            {filtered.map((c, i) => (
              <div
                key={c.iso}
                id={`iv-cc-opt-d-${i}`}
                role="option"
                aria-selected={c.iso === country.iso}
                tabIndex={-1}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pickCountry(c);
                }}
                className={`flex cursor-pointer items-center gap-2.5 px-3.5 py-2.75 font-lato text-[14px] text-rodeo-black hover:bg-light-dust ${
                  i === active ? "bg-light-dust" : ""
                }`}
              >
                <span className="text-[18px] leading-none">{c.flag}</span>
                <span className="flex-1">{c.name}</span>
                <span className="text-[13px] text-dust-ink">{c.dial}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Phone (≤sm): bottom sheet. Always mounted so it can rise/fall; `.open`
          drives the transform, `inert` keeps it out of tab order when closed. */}
      {isPhone && (
        <>
          <button
            type="button"
            aria-label="Close country picker"
            tabIndex={open ? 0 : -1}
            onClick={() => setOpen(false)}
            className={`fixed inset-0 z-10500 cursor-pointer border-none bg-[rgba(51,51,51,0.42)] transition-opacity duration-300 ${
              open ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          />
          <div
            ref={sheetRef}
            role="dialog"
            aria-modal="true"
            aria-label="Select country calling code"
            tabIndex={-1}
            className={`fixed inset-x-0 bottom-0 z-10501 flex max-h-[82vh] flex-col rounded-t-pill bg-clear-dust pb-[max(12px,env(safe-area-inset-bottom))] shadow-[0_-12px_40px_rgba(51,51,51,0.18)] transition-transform duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] ${
              open ? "translate-y-0" : "translate-y-full"
            }`}
          >
            <div className="mx-auto mt-2.5 mb-1 h-1 w-10 flex-none rounded-sm bg-smoke" />
            <div className="flex flex-none items-center justify-between px-5 pt-1.5 pb-2.5">
              <h2 className="m-0 font-crimson text-[22px] font-normal text-rodeo-black">
                Country code
              </h2>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setOpen(false)}
                className="flex h-8.5 w-8.5 flex-none items-center justify-center rounded-full bg-wax text-graphite"
              >
                <Close sx={{ fontSize: 20 }} />
              </button>
            </div>
            <div className="mx-5 mb-1.5 flex flex-none items-center gap-2 rounded-card border border-smoke bg-white px-3.5 py-2.75">
              <Search className="text-dark-dust" sx={{ fontSize: 20 }} />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search countries"
                autoComplete="off"
                role="combobox"
                aria-expanded={open}
                aria-controls="iv-cc-list-mobile"
                aria-autocomplete="list"
                aria-activedescendant={active >= 0 ? `iv-cc-opt-m-${active}` : undefined}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(-1);
                }}
                onKeyDown={onSearchKeyDown}
                className="min-w-0 flex-1 border-none bg-transparent font-lato text-[16px] text-rodeo-black outline-none"
              />
            </div>
            <div
              id="iv-cc-list-mobile"
              role="listbox"
              aria-label="Country calling codes"
              className="overflow-y-auto px-2 pt-1 pb-2"
            >
              {filtered.map((c, i) => (
                <div
                  key={c.iso}
                  id={`iv-cc-opt-m-${i}`}
                  role="option"
                  aria-selected={c.iso === country.iso}
                  tabIndex={-1}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pickCountry(c);
                  }}
                  className={`flex w-full cursor-pointer items-center gap-3 rounded-card px-3.5 py-3.25 text-left font-lato text-[16px] text-rodeo-black ${
                    i === active ? "bg-light-dust" : ""
                  }`}
                >
                  <span className="text-[22px] leading-none">{c.flag}</span>
                  <span className="min-w-0 flex-1">{c.name}</span>
                  <span className="text-[14px] text-dust-ink">{c.dial}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
